import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  additionalSection?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Foundations of Religio-Traditio-Cultural Well-Being',
    content: [
      '<strong>Objective:</strong> Understand how faith, culture, and tradition shape identity, values, and resilience.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• The three pillars: Religion, Culture, and Tradition',
      '• How spirituality differs from religiosity',
      '• Cultural values as a compass for well-being',
      '• The Filipino blend of faith and community',
      '<br/>',
      '<strong>Discussion:</strong> Every individual grows within a cultural and spiritual framework. These foundations influence how we think, cope, relate, and find meaning. In the Filipino context, faith and family traditions remain powerful anchors for mental and emotional stability.'
    ]
  },
  {
    title: 'Exploring the Three Pillars',
    content: [
      '<strong>Religion:</strong> Organized systems of beliefs and practices that connect individuals to the sacred.',
      '<strong>Culture:</strong> Shared values, customs, and traditions that shape our worldview and daily life.',
      '<strong>Tradition:</strong> Practices and rituals passed down through generations, providing continuity and meaning.',
      '<br/>',
      'These pillars interact to form the foundation of personal and community well-being.'
    ]
  },
  {
    title: 'Spirituality vs. Religiosity',
    content: [
      'Spirituality is a personal, internal experience of connection, meaning, and purpose. Religiosity refers to participation in organized religious practices and communities.',
      '<br/>',
      'Both can nurture resilience, but spirituality is often more flexible and individualized.'
    ]
  },
  {
    title: 'Cultural Values as a Compass',
    content: [
      'Cultural values guide our decisions, relationships, and coping strategies. They act as a compass for well-being, helping us navigate challenges and find meaning.',
      '<br/>',
      'In Filipino culture, values like pakikisama (harmony), utang na loob (debt of gratitude), and bayanihan (community spirit) are central to resilience.'
    ]
  },
  {
    title: 'The Filipino Blend of Faith and Community',
    content: [
      'Faith and family traditions are powerful anchors for Filipino mental and emotional stability. Community rituals, celebrations, and shared beliefs foster belonging and support.',
      '<br/>',
      'Reflection: List three family or cultural traditions that give you a sense of belonging. How do they help you feel grounded during stressful times?'
    ],
    additionalSection: {
      title: 'Reflection Activity',
      content: [
        'List three family or cultural traditions that give you a sense of belonging. How do they help you feel grounded during stressful times?'
      ]
    }
  },
  {
    title: 'Key Takeaway',
    content: [
      'Understanding where you come from helps you understand who you are – and what keeps you strong.'
    ]
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You have completed Module 1: Foundations of Religio-Traditio-Cultural Well-Being! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about the foundations of religio-traditio-cultural well-being.',
        'The quiz will help you review key concepts about faith, culture, tradition, and resilience.'
      ]
    }
  }
];

const ReligioTraditioCulturalModule1Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('rtcM1');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/religio-traditio-cultural-module-1/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/religio-traditio-cultural-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left religio-traditional-cultural-panel"
        >
          <div className="module-header">
            <h1>Module 1</h1>
            <p className="module-subtitle">Foundations of Religio-Traditio-Cultural Well-Being</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Understand how faith, culture, and tradition shape identity, values, and resilience.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {safeCurrentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/religio-traditio-cultural-module-1/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReligioTraditioCulturalModule1Page;


